import { z } from 'zod';
import { operatingHoursSchema } from '@vagusx/shared';
import { locationResponseSchema } from '../location/types';
import { spokenNameSchema } from './spoken-name';

// Suggested provider roles used to populate the dropdown. The schema is a
// plain string — legacy data (e.g. "Nurse Practitioner", "Physician") flows
// through unchanged, and operators can type custom values.
export const PROVIDER_ROLES = [
  'Provider',
  'Nurse',
  'Nurse Practitioner',
  'Physician',
  'Physician Assistant',
  'Medical Assistant',
  'Lab Technician',
  'Sleep Technician',
  'Front Desk',
  'Other',
] as const;

export type ProviderRole = (typeof PROVIDER_ROLES)[number];

// Provider response from API.
//
// `credential`, `displayName`, and `spokenName` are computed on read by
// `formatProviderName` (apps/api/src/core/providers/format.ts). EHR-synced
// names routinely arrive with the credential packed into `lastName` and
// tokens duplicated; the helper strips the credential into its own field,
// de-dupes, and produces:
//   - displayName → UI list rendering ("Anurag Sahai, MD")
//   - spokenName  → what the voice agent says (ADR-342): the practice's
//                   `customSpokenName`, else `suggestSpokenName`
//                   ("Doctor Anurag Sahai")
//
// Raw `firstName` / `lastName` stay verbatim so the edit form roundtrips
// the operator's input untouched.
export const providerResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  ehrProviderId: z.string().nullable().optional(),
  pmProviderId: z.string().nullable().optional(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  specialty: z.string().nullable().optional(),
  npi: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  // Provider-selection input (docs/provider-selection.md). Nullable and
  // degrades safely: an unweighted provider scores neutral rather than being
  // dropped from the pool.
  weight: z.number().nullable().optional(),
  // The spoken name the practice wrote (ADR-342), stored; null when it follows
  // the chart, i.e. the agent says `suggestSpokenName`. The edit form reads
  // this; everything that speaks reads `spokenName`.
  customSpokenName: z.string().nullable().optional(),
  credential: z.string().nullable(),
  displayName: z.string(),
  spokenName: z.string(),
  rowStatus: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ProviderResponse = z.infer<typeof providerResponseSchema>;

// Create provider request — error messages render inline on the FE form
// and as fieldErrors on the API. Keep messages user-facing.
export const createProviderSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email format'),
  phone: z.string().optional(),
  specialty: z.string().optional(),
  npi: z.string().optional(),
  gender: z.string().optional(),
  role: z.string().optional(),
  // Range deliberately unconstrained for now — see ADR-141. What a sensible
  // weight looks like will follow from the appointment-type/schedule model
  // change, so pinning a scale here would only have to be undone.
  weight: z.number().optional(),
  // What the voice agent says (ADR-342), abbreviations written out. Blank, or
  // the suggestion itself, follows the chart: the API stores null.
  spokenName: spokenNameSchema.optional(),
  ehrProviderId: z.string().optional(),
  pmProviderId: z.string().optional(),
});

export type CreateProviderRequest = z.infer<typeof createProviderSchema>;

// Update provider request — partial; nullable on optional fields so callers
// can explicitly clear values.
export const updateProviderSchema = z.object({
  firstName: z.string().min(1, 'First name is required').optional(),
  lastName: z.string().min(1, 'Last name is required').optional(),
  email: z.string().email('Invalid email format').optional(),
  phone: z.string().nullable().optional(),
  specialty: z.string().nullable().optional(),
  npi: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  weight: z.number().nullable().optional(),
  spokenName: spokenNameSchema.nullable().optional(),
  ehrProviderId: z.string().nullable().optional(),
  pmProviderId: z.string().nullable().optional(),
});

export type UpdateProviderRequest = z.infer<typeof updateProviderSchema>;

// Assign location request — `hours` overrides the location's default hours
// for this provider only (e.g. provider X works at clinic Y on Tue/Thu only).
export const assignLocationSchema = z.object({
  locationId: z.string().uuid('Invalid location ID'),
  isPrimary: z.boolean().optional(),
  hours: operatingHoursSchema.optional(),
});

export type AssignLocationRequest = z.infer<typeof assignLocationSchema>;

// Provider location response (basic)
export const providerLocationResponseSchema = z.object({
  id: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid(),
  isPrimary: z.boolean(),
});

export type ProviderLocationResponse = z.infer<typeof providerLocationResponseSchema>;

// Provider location with full location data (from GET /providers/:id/locations)
export const providerLocationWithDetailsSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  providerId: z.string().uuid(),
  locationId: z.string().uuid(),
  isPrimary: z.boolean(),
  hours: operatingHoursSchema.nullable().optional(),
  rowStatus: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  location: locationResponseSchema,
});

export type ProviderLocationWithDetails = z.infer<typeof providerLocationWithDetailsSchema>;
